<template>
  <div class="knowledge-list-container">
    <a-page-header title="知识管理" @back="() => null" class="page-header">
      <template #extra>
        <a-button v-if="isAdmin()" @click="handleManageKnowledgeType" style="margin-right: 8px">
          <template #icon><AppstoreOutlined /></template>
          知识类型管理
        </a-button>
        <a-button @click="handleRecycleBin" style="margin-right: 8px">
          <template #icon><DeleteOutlined /></template>
          回收站
        </a-button>
        <a-button type="primary" @click="handleCreate">
          <template #icon><PlusOutlined /></template>
          新建知识
        </a-button>
      </template>
    </a-page-header>

    <a-card class="search-card" :class="{ 'search-card-collapsed': searchFormCollapsed }" :body-style="searchFormCollapsed ? { padding: '0 24px' } : { padding: '16px 24px' }">
      <template #title>
        <div class="search-card-title" @click="toggleSearchForm">
          <span>搜索条件</span>
          <span class="search-card-toggle">
            <UpOutlined v-if="!searchFormCollapsed" />
            <DownOutlined v-else />
            {{ searchFormCollapsed ? '展开' : '收起' }}
          </span>
        </div>
      </template>
      <a-form :model="searchForm" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }" v-show="!searchFormCollapsed">
        <a-form-item label="标题">
          <a-input
            v-model:value="searchForm.title"
            placeholder="请输入标题关键词进行搜索"
            allowClear
            style="width: 100%"
            @pressEnter="handleSearch"
          />
        </a-form-item>
        <a-form-item label="知识类型">
          <div style="display: flex; flex-wrap: wrap; gap: 8px">
            <a-tag
              v-for="type in knowledgeTypes"
              :key="type.id"
              :color="searchForm.types.includes(type.id) ? 'blue' : 'default'"
              style="cursor: pointer; padding: 4px 12px; margin: 0"
              @click="handleTypeToggle(type.id)"
            >
              {{ type.type_name }}
            </a-tag>
          </div>
        </a-form-item>

        <a-form-item label="调用步骤">
          <div style="display: flex; flex-wrap: wrap; gap: 8px">
            <a-tag
              v-for="stepOrder in availableStepOrders"
              :key="stepOrder"
              :color="searchForm.stepOrders.includes(stepOrder) ? 'blue' : 'default'"
              style="cursor: pointer; padding: 4px 12px; margin: 0"
              @click="handleStepOrderToggle(stepOrder)"
            >
              步骤{{ stepOrder }}
            </a-tag>
          </div>
        </a-form-item>

        <a-form-item label="筛选条件">
          <div style="display: flex; flex-wrap: wrap; gap: 8px">
            <a-tooltip
              v-for="tagType in tagTypes"
              :key="tagType.id"
              :title="getTagTypeTooltip(tagType)"
              placement="top"
              :overlay-style="{ whiteSpace: 'pre-line', maxWidth: '400px' }"
            >
              <a-tag
                :color="tagType.checked ? 'blue' : 'default'"
                style="cursor: pointer; padding: 4px 12px; margin: 0; font-weight: 500"
                @click="handleTagTypeClick(tagType)"
              >
                {{ tagType.type_name }}
                <span v-if="tagType.checked && getSelectedTagCount(tagType) > 0" style="margin-left: 4px; font-weight: normal">
                  ({{ getSelectedTagCount(tagType) }})
                </span>
              </a-tag>
            </a-tooltip>
          </div>
        </a-form-item>


        <!-- 标签选择弹窗 -->
        <a-modal
          v-model:open="tagModalVisible"
          :title="currentTagType ? currentTagType.type_name : '选择标签'"
          width="600px"
          @ok="handleTagModalOk"
          @cancel="handleTagModalCancel"
        >
          <div v-if="currentTagType">
            <!-- 加载中提示 -->
            <div v-if="currentTagType.tagsLoading" style="text-align: center; padding: 20px">
              <a-spin size="large" />
              <div style="margin-top: 8px; color: #999">加载标签数据中...</div>
            </div>
            <!-- 联动标签：显示层级结构 -->
            <div v-else-if="currentTagType.is_cascade === 1 && currentTagType.cascadeTags">
              <div v-for="level1 in currentTagType.cascadeTags" :key="level1.id" style="margin-bottom: 16px">
                <div style="font-weight: 500; margin-bottom: 8px; padding: 4px 8px; background: #f5f5f5; border-radius: 4px">
                  {{ level1.value_name }}
                </div>
                <div v-if="level1.children && level1.children.length > 0" style="padding-left: 20px">
                  <div v-for="level2 in level1.children" :key="level2.id" style="margin-bottom: 8px">
                    <div style="font-weight: 500; margin-bottom: 4px; padding: 4px 8px; background: #fafafa; border-radius: 4px">
                      {{ level2.value_name }}
                    </div>
                    <div v-if="level2.children && level2.children.length > 0" style="padding-left: 20px; display: flex; flex-wrap: wrap; gap: 8px">
                      <a-tag
                        v-for="level3 in level2.children"
                        :key="level3.id"
                        :color="isTagSelectedInModal(level3.id) ? 'blue' : 'default'"
                        style="cursor: pointer; padding: 4px 12px; margin: 0"
                        @click="handleTagToggleInModal(level3.id)"
                      >
                        {{ level3.value_name }}
                      </a-tag>
                    </div>
                    <div v-else style="padding-left: 20px; display: flex; flex-wrap: wrap; gap: 8px">
                      <a-tag
                        :color="isTagSelectedInModal(level2.id) ? 'blue' : 'default'"
                        style="cursor: pointer; padding: 4px 12px; margin: 0"
                        @click="handleTagToggleInModal(level2.id)"
                      >
                        {{ level2.value_name }}
                      </a-tag>
                    </div>
                  </div>
                </div>
                <div v-else style="padding-left: 20px; display: flex; flex-wrap: wrap; gap: 8px">
                  <a-tag
                    :color="isTagSelectedInModal(level1.id) ? 'blue' : 'default'"
                    style="cursor: pointer; padding: 4px 12px; margin: 0"
                    @click="handleTagToggleInModal(level1.id)"
                  >
                    {{ level1.value_name }}
                  </a-tag>
                </div>
              </div>
            </div>
            <!-- 独立标签：平铺显示 -->
            <div v-else-if="currentTagType.tagsLoaded && currentTagType.tags && currentTagType.tags.length > 0" style="display: flex; flex-wrap: wrap; gap: 8px">
              <a-tag
                v-for="tag in currentTagType.tags"
                :key="tag.id"
                :color="isTagSelectedInModal(tag.id) ? 'blue' : 'default'"
                style="cursor: pointer; padding: 4px 12px; margin: 0"
                @click="handleTagToggleInModal(tag.id)"
              >
                {{ tag.tag_name || tag.value_name }}
              </a-tag>
            </div>
            <!-- 没有标签或未加载 -->
            <div v-else style="text-align: center; padding: 20px; color: #999">
              <div v-if="!currentTagType.tagsLoaded && !currentTagType.tagsLoading">标签数据未加载</div>
              <div v-else-if="currentTagType.tagsLoaded">暂无标签数据</div>
            </div>
          </div>
        </a-modal>

        <a-form-item label="状态筛选">
          <a-radio-group v-model:value="searchForm.isDeleted" @change="handleSearch">
            <a-radio-button :value="0">启用</a-radio-button>
            <a-radio-button :value="1">禁用</a-radio-button>
          </a-radio-group>
          <span style="margin-left: 12px; color: #999; font-size: 12px;">
            {{ searchForm.isDeleted === 1 ? '显示已软删除的知识（回收站内容）' : '显示正常启用的知识' }}
          </span>
        </a-form-item>

        <a-form-item label="版本筛选">
          <a-radio-group v-model:value="searchForm.versionStrategy" @change="handleSearch">
            <a-radio-button value="published">
              <CheckCircleOutlined /> 生产环境版本
            </a-radio-button>
            <a-radio-button value="latest">
              <EditOutlined /> 预发布环境版本
            </a-radio-button>
          </a-radio-group>
          <span style="margin-left: 12px; color: #999; font-size: 12px;">
            {{ searchForm.versionStrategy === 'published' ? '筛选生产环境版本的标签/步骤' : '筛选预发布环境版本的标签/步骤' }}
          </span>
        </a-form-item>

        <a-form-item :wrapper-col="{ offset: 4, span: 20 }">
          <a-button type="primary" @click="handleSearch" :loading="loading">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card class="table-card">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
        :scroll="{ x: 'max-content' }"
        class="knowledge-table"
      >
        <template #emptyText>
          <span>数据加载中...</span>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'action'">
            <template v-if="searchForm.isDeleted === 1">
              <a-popconfirm title="确定恢复此知识吗？" @confirm="handleRestore(record.id)">
                <a-button type="link">恢复</a-button>
              </a-popconfirm>
            </template>
            <template v-else>
              <a-button type="link" @click="handleEdit(record)">编辑</a-button>
              <a-button type="link" @click="handleViewHistory(record)">
                <template #icon><HistoryOutlined /></template>
                版本
              </a-button>
              <a-popconfirm title="确定删除吗？" @confirm="handleDelete(record.id)">
                <a-button type="link" danger>删除</a-button>
              </a-popconfirm>
            </template>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="record.status === 1 ? 'green' : 'red'">
              {{ record.status === 1 ? '启用' : '禁用' }}
            </a-tag>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted, h, nextTick, computed } from 'vue';
import { useRouter } from 'vue-router';
import { message, Tag, Tooltip } from 'ant-design-vue';
import { PlusOutlined, AppstoreOutlined, UpOutlined, DownOutlined, DeleteOutlined, ExclamationCircleFilled, HistoryOutlined, CheckCircleOutlined, EditOutlined, ExportOutlined } from '@ant-design/icons-vue';
import { searchKnowledge, deleteKnowledge, restoreKnowledge, getVisibleKnowledgeTypes } from '../api/knowledge';
import { useAuth } from '../composables/useAuth';
import { getTagTypes, getTagsByType, getCascadeTags } from '../api/tagType';

export default {
  name: 'KnowledgeList',
  components: {
    PlusOutlined,
    AppstoreOutlined,
    UpOutlined,
    DownOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    HistoryOutlined,
    CheckCircleOutlined,
    EditOutlined,
    ExportOutlined
  },
  setup() {
    const router = useRouter();
    const { isAdmin } = useAuth();
    const loading = ref(false);
    const dataSource = ref([]);
    const knowledgeTypes = ref([]);
    const tagTypes = ref([]);
    const tagModalVisible = ref(false); // 标签选择弹窗显示状态
    const currentTagType = ref(null); // 当前正在选择标签的标签类型
    const tempSelectedTagIds = ref([]); // 弹窗中临时选中的标签ID
    const searchFormCollapsed = ref(false); // 搜索表单是否折叠（默认展开）
    const searchForm = reactive({
      title: '',
      types: [],
      stepOrders: [], // 步骤序号数组：1, 2, 3, ...
      conditions: {},
      must: [],
      versionStrategy: 'published', // 版本筛选策略：'published' 或 'latest'
      isDeleted: 0 // 0=启用(默认), 1=禁用(软删除)
    });
    
    // 可用的步骤序号（1-9，根据实际业务需求调整）
    const availableStepOrders = ref([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    // 使用 ref 存储分页状态
    const paginationCurrent = ref(1);
    const paginationPageSize = ref(20);
    const paginationTotal = ref(0);
    
    // 使用计算属性生成分页配置对象
    const pagination = computed(() => {
      console.log('pagination computed 被调用:', {
        current: paginationCurrent.value,
        pageSize: paginationPageSize.value,
        total: paginationTotal.value
      });
      return {
        current: paginationCurrent.value,
        pageSize: paginationPageSize.value,
        total: paginationTotal.value,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => {
          console.log('showTotal 被调用:', total, range);
          return `共 ${total} 条，显示 ${range[0]}-${range[1]} 条`;
        },
        pageSizeOptions: ['10', '20', '50', '100'],
        hideOnSinglePage: false // 即使只有一页也显示分页器
      };
    });

    // 知识类型颜色映射
    const knowledgeTypeColors = {
      'prompt': 'gold',      // 提示词 - 淡黄色
      'dataflow': 'lime',    // 数据流 - 淡绿色
      'pin_info': 'cyan',    // 针脚信息 - 淡青色
      'case': 'purple',      // 典型案例 - 淡紫色
      'principle': 'orange'   // 系统原理 - 淡橙色
    };

    // 调用步骤颜色列表（循环使用）
    const stepColors = ['blue', 'geekblue', 'green', 'lime', 'gold', 'orange', 'cyan', 'purple'];

    // 获取知识类型颜色
    const getKnowledgeTypeColor = (typeCode) => {
      return knowledgeTypeColors[typeCode?.toLowerCase()] || 'default';
    };

    // 获取调用步骤颜色（根据步骤ID或名称）
    const getStepColor = (stepId, stepName) => {
      if (!stepId && !stepName) return 'default';
      // 使用步骤ID或名称的哈希值来选择颜色
      const hash = (stepId || stepName || '').toString().split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);
      return stepColors[Math.abs(hash) % stepColors.length];
    };

    // 格式化时间为北京时间 (YY/MM/DD HH:mm:ss)
    const formatBeijingTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      // 使用 toLocaleString 转换为北京时间
      const options = {
        timeZone: 'Asia/Shanghai',
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };
      // 格式化为 YY/MM/DD HH:mm:ss
      const formatted = date.toLocaleString('zh-CN', options);
      // 将 YYYY/M/D 转换为 YY/MM/DD 格式
      return formatted.replace(/(\d{4})\/(\d{1,2})\/(\d{1,2})/, (match, year, month, day) => {
        return `${year.slice(-2)}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
      });
    };

    // 在新标签页打开编辑页面
    const handleEditInNewTab = (record) => {
      const routeData = router.resolve({ name: 'knowledge-edit', params: { id: record.id } });
      window.open(routeData.href, '_blank');
    };

    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { 
        title: '标题', 
        key: 'title', 
        width: 250,
        customRender: ({ record }) => {
          const hasUnpublishedChanges = record.latest_version > (record.published_version || 0);
          return h('div', { style: 'display: flex; align-items: center; gap: 6px;' }, [
            // 点击标题，当前页面打开编辑页面
            h('a', { 
              style: 'color: #1890ff; cursor: pointer;',
              onClick: () => handleEdit(record)
            }, record.title),
            // 有未发布更改的图标
            hasUnpublishedChanges ? h(Tooltip, { title: '有未发布的更改' }, {
              default: () => h(ExclamationCircleFilled, { style: 'color: #fa8c16; font-size: 14px;' })
            }) : null,
            // 新标签打开图标
            h(Tooltip, { title: '在新标签页打开' }, {
              default: () => h(ExportOutlined, { 
                style: 'color: #1890ff; font-size: 14px; cursor: pointer;',
                onClick: (e) => {
                  e.stopPropagation();
                  handleEditInNewTab(record);
                }
              })
            })
          ]);
        }
      },
      { 
        title: '知识类型', 
        key: 'type_name', 
        width: 120,
        customRender: ({ record }) => {
          // 如果 type_name 为空，尝试从 type_id 获取，或者显示默认值
          if (!record.type_name) {
            // 如果 type_id 存在但 type_name 为空，可能是 JOIN 失败，显示 type_id
            if (record.type_id) {
              return h('span', { style: 'color: #999;' }, `类型ID: ${record.type_id}`);
            }
            return h('span', { style: 'color: #999;' }, '-');
          }
          const color = getKnowledgeTypeColor(record.type_code);
          return h(Tag, { 
            color: color,
            style: 'border: 1px solid; background: rgba(255, 255, 255, 0.1); opacity: 0.8;'
          }, { default: () => record.type_name });
        }
      },
      { 
        title: '调用步骤', 
        key: 'step_name', 
        width: 150,
        customRender: ({ record }) => {
          // 获取知识关联的步骤（step_order：1-9）
          const steps = record.steps || [];
          if (steps.length === 0) {
            // 如果没有关联步骤，显示"全部支持"
            return h(Tag, { 
              color: 'green',
              style: 'border: 1px solid; background: rgba(255, 255, 255, 0.1); opacity: 0.8;'
            }, { default: () => '全部支持' });
          }
          
          // 显示所有关联的步骤（步骤1-9）
          const stepOrders = steps.map(s => s.step_order).filter(so => so >= 1 && so <= 9).sort((a, b) => a - b);
          if (stepOrders.length === 0) {
            return '-';
          }
          
          // 如果有多个步骤，显示多个标签
          return h('div', { style: 'display: flex; flex-wrap: wrap; gap: 4px;' }, 
            stepOrders.map(stepOrder => {
              const stepName = `步骤${stepOrder}`;
              const color = getStepColor(stepOrder, stepName);
              return h(Tag, { 
                key: stepOrder,
                color: color,
                style: 'border: 1px solid; background: rgba(255, 255, 255, 0.1); opacity: 0.8;'
              }, { default: () => stepName });
            })
          );
        }
      },
      { 
        title: '筛选条件', 
        key: 'tags', 
        width: 300,
        customRender: ({ record }) => {
          if (!record.tags || record.tags.length === 0) {
            return '-';
          }
          // 按标签类型分组显示
          const tagsByType = {};
          record.tags.forEach(tag => {
            const typeCode = tag.tag_type_code;
            const typeName = tag.type_name || typeCode;
            if (!tagsByType[typeCode]) {
              tagsByType[typeCode] = { name: typeName, values: [] };
            }
            // 对于联动标签，优先使用 tag_path（完整路径），否则使用 tag_value
            // 对于独立标签，使用 tag_name 或 tag_value
            let tagValue;
            if (tag.tag_category === 'cascade' && tag.tag_path) {
              tagValue = tag.tag_path; // 使用完整路径：第一级 > 第二级 > 第三级
            } else {
              tagValue = tag.tag_value || tag.tag_name || tag.value_name;
            }
            if (tagValue) {
              tagsByType[typeCode].values.push(tagValue);
            }
          });
          
          const tagElements = [];
          Object.entries(tagsByType).forEach(([typeCode, data]) => {
            const tags = data.values.map(value => 
              h(Tag, { key: value, style: 'margin: 2px 4px 2px 0;' }, { default: () => value })
            );
            tagElements.push(
              h('div', { key: typeCode, style: 'margin-bottom: 6px;' }, [
                h('span', { style: 'font-weight: 500; color: #666; margin-right: 4px; font-size: 12px;' }, `${data.name}:`),
                ...tags
              ])
            );
          });
          
          return h('div', { style: 'max-height: 100px; overflow-y: auto;' }, tagElements);
        }
      },
      { 
        title: '版本', 
        key: 'version', 
        width: 100,
        customRender: ({ record }) => {
          const latest = record.latest_version || 1;
          const published = record.published_version || latest;
          return h('div', { style: 'display: flex; flex-direction: column; gap: 2px; font-size: 12px;' }, [
            h('span', {}, `预发布: v${latest}`),
            h('span', { style: 'color: #52c41a;' }, `生产: v${published}`)
          ]);
        }
      },
      { 
        title: '创建时间', 
        key: 'create_time', 
        width: 150,
        customRender: ({ record }) => {
          return h('span', {}, formatBeijingTime(record.create_time));
        }
      },
      { title: '操作', key: 'action', width: 200, fixed: 'right' }
    ];

    // 加载基础数据
    const loadData = async () => {
      try {
        const [typesRes, tagTypesRes] = await Promise.all([
          getVisibleKnowledgeTypes(),
          getTagTypes()
        ]);
        knowledgeTypes.value = typesRes.data || [];
        
        // 初始化标签类型（不加载标签数据，按需加载）
        const tagTypesData = tagTypesRes.data || [];
        for (const tagType of tagTypesData) {
          tagType.checked = false;
          tagType.tags = [];
          tagType.cascadeTags = null; // 联动标签的层级结构
          tagType.tagsLoaded = false; // 标记标签数据是否已加载
          tagType.tagsLoading = false; // 标记标签数据是否正在加载
        }
        
        tagTypes.value = tagTypesData;
        
        // 数据加载完成后，延迟执行初始搜索（使用默认排序：按编辑时间倒序）
        // 使用 setTimeout 让浏览器有机会渲染页面
        setTimeout(() => {
          handleSearch();
        }, 100);
      } catch (error) {
        message.error('加载数据失败');
      }
    };

    // 扁平化联动标签（获取所有最终层级的标签）
    const flattenCascadeTags = (cascadeData) => {
      const result = [];
      const traverse = (items) => {
        items.forEach(item => {
          if (item.children && item.children.length > 0) {
            traverse(item.children);
          } else {
            // 叶子节点
            result.push(item);
          }
        });
      };
      traverse(cascadeData);
      return result;
    };

    // 切换搜索表单折叠状态
    const toggleSearchForm = () => {
      searchFormCollapsed.value = !searchFormCollapsed.value;
    };

    // 知识类型切换
    const handleTypeToggle = (typeId) => {
      const index = searchForm.types.indexOf(typeId);
      if (index > -1) {
        searchForm.types.splice(index, 1);
      } else {
        searchForm.types.push(typeId);
      }
    };

    // 步骤序号切换
    const handleStepOrderToggle = (stepOrder) => {
      const index = searchForm.stepOrders.indexOf(stepOrder);
      if (index > -1) {
        searchForm.stepOrders.splice(index, 1);
      } else {
        searchForm.stepOrders.push(stepOrder);
      }
    };

    // 点击标签类型：打开弹窗（按需加载标签数据）
    const handleTagTypeClick = async (tagType) => {
      currentTagType.value = tagType;
      
      // 如果标签数据未加载，则按需加载
      if (!tagType.tagsLoaded && !tagType.tagsLoading) {
        tagType.tagsLoading = true;
        try {
          if (tagType.is_cascade === 1) {
            // 联动标签：保存层级结构用于弹窗显示
            const cascadeRes = await getCascadeTags(tagType.id);
            tagType.cascadeTags = cascadeRes.data || [];
            // 同时保存扁平化的标签用于搜索
            tagType.tags = flattenCascadeTags(cascadeRes.data || []);
          } else {
            // 独立标签
            const tagsRes = await getTagsByType(tagType.id);
            tagType.tags = tagsRes.data || [];
          }
          tagType.tagsLoaded = true;
        } catch (error) {
          console.error(`加载标签类型 ${tagType.type_name} 的标签失败:`, error);
          message.error(`加载标签失败: ${tagType.type_name}`);
        } finally {
          tagType.tagsLoading = false;
        }
      }
      
      // 初始化弹窗中已选中的标签ID（从searchForm中获取）
      tempSelectedTagIds.value = searchForm.conditions[tagType.type_code] ? [...searchForm.conditions[tagType.type_code]] : [];
      tagModalVisible.value = true;
    };

    // 弹窗中标签切换
    const handleTagToggleInModal = (tagId) => {
      const index = tempSelectedTagIds.value.indexOf(tagId);
      if (index > -1) {
        tempSelectedTagIds.value.splice(index, 1);
      } else {
        tempSelectedTagIds.value.push(tagId);
      }
    };

    // 检查弹窗中标签是否被选中
    const isTagSelectedInModal = (tagId) => {
      return tempSelectedTagIds.value.includes(tagId);
    };

    // 弹窗确定：保存选中的标签
    const handleTagModalOk = () => {
      if (currentTagType.value) {
        if (tempSelectedTagIds.value.length > 0) {
          // 有选中的标签，设置为必选
          searchForm.conditions[currentTagType.value.type_code] = [...tempSelectedTagIds.value];
          currentTagType.value.checked = true;
        } else {
          // 没有选中的标签，清除
          delete searchForm.conditions[currentTagType.value.type_code];
          currentTagType.value.checked = false;
        }
        updateMustFields();
      }
      tagModalVisible.value = false;
      currentTagType.value = null;
      tempSelectedTagIds.value = [];
    };

    // 弹窗取消
    const handleTagModalCancel = () => {
      tagModalVisible.value = false;
      currentTagType.value = null;
      tempSelectedTagIds.value = [];
    };

    // 检查标签是否被选中（已废弃，保留用于兼容）
    const isTagSelected = (tagTypeCode, tagId) => {
      const tagIds = searchForm.conditions[tagTypeCode];
      return tagIds && tagIds.includes(tagId);
    };

    // 更新must字段（选中就是必选）
    const updateMustFields = () => {
      searchForm.must = tagTypes.value
        .filter(tt => tt.checked)
        .map(tt => tt.type_code);
    };

    // 获取标签类型选中的标签数量
    const getSelectedTagCount = (tagType) => {
      const tagIds = searchForm.conditions[tagType.type_code];
      return tagIds ? tagIds.length : 0;
    };

    // 获取标签类型的 Tooltip 内容（显示选中的标签列表）
    const getTagTypeTooltip = (tagType) => {
      const tagIds = searchForm.conditions[tagType.type_code];
      if (!tagIds || tagIds.length === 0) {
        return '点击选择标签';
      }

      // 获取选中的标签信息
      const selectedTags = [];
      if (tagType.is_cascade === 1 && tagType.cascadeTags) {
        // 联动标签：需要构建层级路径
        const findTagPath = (items, targetId, path = []) => {
          for (const item of items) {
            const currentPath = [...path, item.value_name];
            if (item.id === targetId) {
              return currentPath.join(' > ');
            }
            if (item.children && item.children.length > 0) {
              const result = findTagPath(item.children, targetId, currentPath);
              if (result) return result;
            }
          }
          return null;
        };

        for (const tagId of tagIds) {
          const path = findTagPath(tagType.cascadeTags, tagId);
          if (path) {
            selectedTags.push(path);
          } else {
            // 如果找不到路径，尝试从扁平化的 tags 中查找
            const tag = tagType.tags.find(t => t.id === tagId);
            if (tag) {
              selectedTags.push(tag.value_name || tag.tag_name);
            }
          }
        }
      } else {
        // 独立标签
        for (const tagId of tagIds) {
          const tag = tagType.tags.find(t => t.id === tagId);
          if (tag) {
            selectedTags.push(tag.tag_name || tag.value_name);
          }
        }
      }

      if (selectedTags.length === 0) {
        return '点击选择标签';
      }

      // 如果标签太多，只显示前5个，后面用省略号
      const displayTags = selectedTags.slice(0, 5);
      const moreCount = selectedTags.length - 5;
      let tooltipText = displayTags.join('、');
      if (moreCount > 0) {
        tooltipText += `\n... 还有 ${moreCount} 项`;
      }
      return tooltipText;
    };

    // 搜索
    const handleSearch = async () => {
      console.log('handleSearch 被调用');
      loading.value = true;
      try {
        const params = {
          title: searchForm.title && searchForm.title.trim() ? searchForm.title.trim() : null,
          types: searchForm.types.length > 0 ? searchForm.types : [],
          stepOrders: searchForm.stepOrders && searchForm.stepOrders.length > 0 ? searchForm.stepOrders : [],
          conditions: searchForm.conditions,
          must: searchForm.must,
          versionStrategy: searchForm.versionStrategy, // 版本筛选策略
          isDeleted: searchForm.isDeleted, // 状态筛选：0=启用, 1=禁用
          sort: 'update_time',
          order: 'desc',
          page: paginationCurrent.value,
          pageSize: paginationPageSize.value
        };
        const res = await searchKnowledge(params);
        console.log('搜索响应数据:', res);
        
        // 响应拦截器已经返回了 res，结构是 {code: 200, message: 'success', data: {...}}
        // 所以实际数据在 res.data 中
        const responseData = res.data || {};
        const items = responseData.items || [];
        const total = responseData.total || 0;
        
        dataSource.value = items;
        
        // 更新分页信息
        paginationTotal.value = total;
        if (responseData.page !== undefined && responseData.page !== null) {
          paginationCurrent.value = responseData.page;
        }
        if (responseData.pageSize !== undefined && responseData.pageSize !== null) {
          paginationPageSize.value = responseData.pageSize;
        }
        
        console.log('分页信息更新:', {
          current: paginationCurrent.value,
          pageSize: paginationPageSize.value,
          total: paginationTotal.value,
          dataTotal: total,
          responseData: responseData,
          paginationComputed: pagination.value
        });
        
        // 使用 nextTick 确保 DOM 更新
        await nextTick();
        console.log('nextTick 后的分页信息:', {
          current: paginationCurrent.value,
          pageSize: paginationPageSize.value,
          total: paginationTotal.value,
          dataSourceLength: dataSource.value.length,
          paginationComputed: pagination.value
        });
      } catch (error) {
        message.error('查询失败');
      } finally {
        loading.value = false;
      }
    };

    // 重置
    const handleReset = () => {
      searchForm.title = '';
      searchForm.types = [];
      searchForm.stepOrders = [];
      searchForm.conditions = {};
      searchForm.must = [];
      searchForm.versionStrategy = 'published'; // 重置版本策略
      searchForm.isDeleted = 0; // 重置状态筛选
      tagTypes.value.forEach(tt => {
        tt.checked = false;
      });
      paginationCurrent.value = 1;
      paginationTotal.value = 0;
      dataSource.value = [];
    };

    // 表格变化（分页、排序等）
    const handleTableChange = (pag, filters, sorter) => {
      console.log('handleTableChange 被调用:', pag, filters, sorter);
      if (pag) {
        paginationCurrent.value = pag.current;
        paginationPageSize.value = pag.pageSize;
        handleSearch();
      }
    };

    // 知识类型管理
    const handleManageKnowledgeType = () => {
      router.push({ name: 'knowledge-type' });
    };

    // 新建
    const handleCreate = () => {
      router.push({ name: 'knowledge-create' });
    };

    // 编辑
    const handleEdit = (record) => {
      router.push({ name: 'knowledge-edit', params: { id: record.id } });
    };

    // 删除
    const handleDelete = async (id) => {
      try {
        await deleteKnowledge(id);
        message.success('删除成功');
        loadData();
      } catch (error) {
        message.error('删除失败');
      }
    };

    // 恢复软删除的知识
    const handleRestore = async (id) => {
      try {
        await restoreKnowledge(id);
        message.success('恢复成功');
        handleSearch();
      } catch (error) {
        message.error('恢复失败');
      }
    };

    // 查看版本历史
    const handleViewHistory = (record) => {
      router.push({ name: 'knowledge-version-history', params: { id: record.id } });
    };

    // 回收站
    const handleRecycleBin = () => {
      router.push({ name: 'knowledge-recycle-bin' });
    };

    onMounted(() => {
      loadData();
    });

    return {
      loading,
      dataSource,
      knowledgeTypes,
      tagTypes,
      tagModalVisible,
      currentTagType,
      tempSelectedTagIds,
      searchFormCollapsed,
      searchForm,
      availableStepOrders,
      pagination,
      columns,
      handleTypeToggle,
      handleStepOrderToggle,
      handleTagTypeClick,
      handleTagToggleInModal,
      isTagSelectedInModal,
      handleTagModalOk,
      handleTagModalCancel,
      getSelectedTagCount,
      getTagTypeTooltip,
      toggleSearchForm,
      handleSearch,
      handleReset,
      handleTableChange,
      handleManageKnowledgeType,
      isAdmin,
      handleCreate,
      handleEdit,
      handleDelete,
      handleRestore,
      handleViewHistory,
      handleRecycleBin,
      ExclamationCircleFilled,
      HistoryOutlined
    };
  }
};
</script>

<style scoped>
.knowledge-list-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f0f2f5;
}

.page-header {
  flex-shrink: 0;
  background: #fff;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  margin: 0;
}

.search-card {
  flex-shrink: 0;
  margin: 16px 16px 0 16px;
  transition: all 0.3s ease;
}

.table-card {
  margin: 16px;
  flex-shrink: 0;
}

.knowledge-table {
  width: 100%;
}

/* 优化搜索表单折叠时的显示 */
.search-card :deep(.ant-card-head) {
  padding: 12px 24px;
  min-height: 48px;
}

.search-card :deep(.ant-card-body) {
  padding: 16px 24px;
}

/* 搜索卡片标题区域 - 整个区域可点击 */
.search-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  width: 100%;
  transition: opacity 0.2s;
}

.search-card-title:hover {
  opacity: 0.8;
}

/* 切换按钮样式 */
.search-card-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #1890ff;
  font-size: 14px;
}

/* 折叠时的样式优化 */
.search-card-collapsed :deep(.ant-card-head) {
  padding: 8px 24px;
  min-height: 40px;
}

.search-card-collapsed :deep(.ant-card-body) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 24px;
  padding-right: 24px;
}
</style>
